import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  IconButton,
  Snackbar,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CloseIcon from "@mui/icons-material/Close";
import BackendAxios from "../../BackendAxios";
import DetailsFieldsPanel from "../../BrowseDesigns/components/DetailsFieldsPanel";
import DetailsInfoDrawer from "../../BrowseDesigns/components/DetailsInfoDrawer";
import DetailsMediaPanel from "../../BrowseDesigns/components/DetailsMediaPanel";
import {
  formatNumber,
  isSquareFootageType,
  parseSquareFootageValue,
  sortFieldValuesForDisplay,
} from "../../BrowseDesigns/components/detailsDialogUtils";
import "../../BrowseDesigns/styles.css";

function GalleryPlanOverlay(props) {
  const { open, entry, onBack, onClose, isPhone, sizing } = props;
  const [activeIndex, setActiveIndex] = useState(0);
  const [infoOpen, setInfoOpen] = useState(false);
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [statusOpen, setStatusOpen] = useState(false);
  const [statusText, setStatusText] = useState("");
  const [statusSeverity, setStatusSeverity] = useState("success");
  const phoneNavOffset = Number(sizing?.xs?.usedWidth || 0);

  const images = useMemo(
    () =>
      Array.isArray(entry?.images)
        ? entry.images.filter((image) => image?.url)
        : [],
    [entry]
  );
  const referenceImageUrl = useMemo(() => {
    const referenceImage =
      images.find(
        (image) =>
          String(image?.type || "").trim().toLowerCase() === "cover" &&
          (image?.previewUrl || image?.url)
      ) || images.find((image) => image?.previewUrl || image?.url);

    return referenceImage?.previewUrl || referenceImage?.url || "";
  }, [images]);

  const fieldValues = useMemo(
    () => sortFieldValuesForDisplay(entry?.fieldValues),
    [entry]
  );

  const totalSquareFeet = useMemo(() => {
    const totals = fieldValues.reduce(
      (acc, fieldValue) => {
        if (!isSquareFootageType(fieldValue?.fieldId?.type)) return acc;

        const parsed = parseSquareFootageValue(fieldValue?.value);
        if (parsed === null) return acc;

        return {
          value: acc.value + parsed,
          count: acc.count + 1,
        };
      },
      { value: 0, count: 0 }
    );

    return totals.count > 0 ? formatNumber(totals.value) : null;
  }, [fieldValues]);

  useEffect(() => {
    setActiveIndex(0);
    setInfoOpen(false);
    setName("");
    setPhone("");
    setEmail("");
    setMessage("");
  }, [entry?._id, open]);

  const sendInfoRequest = async () => {
    if (sending) return;

    setSending(true);
    try {
      const entryName = entry?.name || "Unknown Entry";
      const referenceHeader = `In Reference to ${entryName}`;
      await BackendAxios.post("/email/send", {
        name,
        phone,
        email,
        message: `${referenceHeader}\n\n${message}`,
        referenceImageUrl,
      });

      setName("");
      setPhone("");
      setEmail("");
      setMessage("");
      setStatusSeverity("success");
      setStatusText("Your request has been sent.");
      setStatusOpen(true);
      setInfoOpen(false);
    } catch (error) {
      setStatusSeverity("error");
      setStatusText("Failed to send request.");
      setStatusOpen(true);
    } finally {
      setSending(false);
    }
  };

  return (
    <Box
      sx={{
        position: "absolute",
        inset: 0,
        zIndex: 4,
        display: "flex",
        flexDirection: "column",
        backgroundColor: "#fff",
        color: "#2e3641",
        transform: open ? "translateX(0)" : "translateX(101%)",
        opacity: open ? 1 : 0,
        pointerEvents: open ? "auto" : "none",
        transition: "transform 240ms ease, opacity 240ms ease",
        overflow: "hidden",
        pr: isPhone ? `${phoneNavOffset}px` : 0,
        boxSizing: "border-box",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
          px: 2,
          py: 1.25,
          borderBottom: "1px solid rgba(0,0,0,0.12)",
          flexShrink: 0,
        }}
      >
        <Button startIcon={<ArrowBackIcon />} onClick={onBack}>
          Back
        </Button>
        <Typography
          variant="subtitle1"
          sx={{
            fontFamily: '"Alegreya Sans SC", "Trebuchet MS", sans-serif',
            letterSpacing: "0.06em",
            textTransform: "uppercase",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            flex: 1,
            textAlign: "center",
          }}
        >
          {entry?.name || "Details"}
        </Typography>
        <IconButton onClick={onClose} sx={{ display: isPhone ? "none" : "inline-flex" }}>
          <CloseIcon />
        </IconButton>
      </Box>

      <Box
        className="browse-designs-details-content"
        sx={{
          flex: 1,
          minHeight: 0,
          overflowX: "hidden",
          background: "#fff",
        }}
      >
        <Box className="browse-designs-details-content-inner" sx={{ p: 2 }}>
          <DetailsMediaPanel
            entry={entry}
            images={images}
            activeIndex={activeIndex}
            setActiveIndex={setActiveIndex}
          />

          <DetailsFieldsPanel
            entry={entry}
            fieldValues={fieldValues}
            totalSquareFeet={totalSquareFeet}
          />
        </Box>

        <Box
          className={`browse-designs-details-info-overlay${
            infoOpen ? " is-open" : ""
          }`}
        >
          <DetailsInfoDrawer
            open={infoOpen}
            entryName={entry?.name}
            images={images}
            name={name}
            phone={phone}
            email={email}
            message={message}
            sending={sending}
            onNameChange={setName}
            onPhoneChange={setPhone}
            onEmailChange={setEmail}
            onMessageChange={setMessage}
            onSubmit={sendInfoRequest}
          />
        </Box>
      </Box>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "1fr auto 1fr",
          alignItems: "center",
          gap: 1,
          px: 2,
          py: 1.25,
          borderTop: "1px solid rgba(0,0,0,0.12)",
          flexShrink: 0,
          backgroundColor: "#fff",
          pb: isPhone ? "calc(10px + env(safe-area-inset-bottom, 0px))" : 1.25,
        }}
      >
        <Box />
        <Button
          variant="contained"
          size="small"
          onClick={() => setInfoOpen((prev) => !prev)}
          sx={{ background: "#5f738f", "&:hover": { background: "#4d6079" } }}
        >
          {infoOpen ? "Hide Information Form" : "Get More Information"}
        </Button>
        <Box sx={{ justifySelf: "end" }}>
          <Button onClick={onClose}>Close</Button>
        </Box>
      </Box>

      <Snackbar
        open={statusOpen}
        autoHideDuration={5000}
        onClose={() => setStatusOpen(false)}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
      >
        <Alert
          onClose={() => setStatusOpen(false)}
          severity={statusSeverity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {statusText}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default GalleryPlanOverlay;
